<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import { useTranslation } from "i18next-vue";
import { useComponentErrorCapture } from "@/mixins/error";
import { useTiming } from "@/mixins/timing";
import { isTouchEnabled } from "@/mixins/device";
import { useModal } from "@/mixins/modal";
import { hideMessage } from "@/stores/messages";

const { t } = useTranslation();
const { sleep } = useTiming();
const { registerOpenModal, unregisterModal } = useModal();
useComponentErrorCapture();

const emit = defineEmits(["close", "submit"]);

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: false,
  },
  show: {
    type: Boolean,
    default: false,
  },
  closeable: {
    type: Boolean,
    default: true,
  },
});

const modal = ref<HTMLDivElement | null>(null);

watch(props, async () => {
  if (props.show) {
    registerOpenModal(props.name, !props.closeable);
    if (!isTouchEnabled()) {
      await sleep(200);
      const focusElement = modal.value as HTMLDivElement;
      let input;
      if (focusElement) {
        input = focusElement.querySelector("input, textarea") as HTMLDivElement;
        if (input?.closest("[data-nofocus]")) {
          return;
        }
      }
      (input ? input : focusElement)?.focus();
    }
  } else {
    unregisterModal(props.name, !props.closeable);
  }
});

function triggerClose() {
  if (!props.closeable) {
    return;
  }
  hideMessage()
  emit("close");
}

function triggerSubmit() {
  emit("submit");
}

function handleKeyup(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    e.stopPropagation()
    emit("close");
  }
}

onMounted(() => {
  window.addEventListener('keyup', handleKeyup)
})

onBeforeUnmount(() => {
  window.removeEventListener('keyup', handleKeyup)
})

</script>
<template>
  <Transition name="opacity-fast">
    <div
      v-if="show"
      class="modal-minimal"
      @keyup.enter.stop="triggerSubmit"
      ref="modal"
    >
      <div class="modal-minimal__background" @click="triggerClose"></div>
      <div class="modal-minimal__container">
        <span v-if="title" class="modal-minimal__title">
          {{ title }}
        </span>
        <div class="modal-minimal__body">
          <slot />
        </div>
        <div class="modal-minimal__footer" v-if="!!$slots.footer">
          <slot name="footer" />
        </div>
      </div>
    </div>
  </Transition>
</template>
